import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, 
  Check, 
  X, 
  ExternalLink, 
  RefreshCw,
  AlertCircle,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Security audit (FIN-2 T1, 2026-04-30) — sections legacy.
 *
 * Backend já blindado pela SEC-1. Sem JWT, fetch retorna 401 e UI quebra.
 * /api/auth/google/callback (Categoria C) intencionalmente sem JWT — OAuth
 * redirect público, state vem de /start onde foi setado a partir do JWT.
 *
 * | Endpoint chamado                            | authFetch usado? | Notas       |
 * |---------------------------------------------|------------------|-------------|
 * | GET  /api/auth/google/status?user_id=       | ❌ → ✅ (T2)     | status conn |
 * | GET  /api/auth/google/start?user_id=        | ❌ → ✅ (T2)     | iniciar     |
 * | DEL  /api/auth/google/disconnect?user_id=   | ❌ → ✅ (T2)     | desconectar |
 *
 * Nota: query user_id mantido por enquanto pra não mudar shape — backend
 * usa current_user["id"] do JWT, ignorando o param.
 */

export default function GoogleCalendarConnect({ userId = "default_user" }) {
  const [status, setStatus] = useState({
    connected: false,
    configured: false,
    email: null,
    loading: true
  });
  const [isConnecting, setIsConnecting] = useState(false);

  // Check connection status on mount and after URL params
  const checkStatus = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/api/auth/google/status?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setStatus({
          connected: data.connected,
          configured: data.configured,
          email: data.email || null,
          loading: false
        });
      }
    } catch (error) {
      console.error("Failed to check Google Calendar status:", error);
      setStatus(prev => ({ ...prev, loading: false }));
    }
  }, [userId]);

  useEffect(() => {
    checkStatus();
    
    // Check URL params for OAuth callback result
    const params = new URLSearchParams(window.location.search);
    const googleConnected = params.get("google_connected");
    const email = params.get("email");
    
    if (googleConnected === "true") {
      toast.success(`Google Calendar conectado! (${email || ""})`);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      checkStatus();
    }
  }, [checkStatus]);

  const handleConnect = async () => {
    setIsConnecting(true);
    
    try {
      const response = await authFetch(`${API_URL}/api/auth/google/start?user_id=${userId}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to start OAuth flow");
      }
      
      const data = await response.json();
      
      // Redirect to Google OAuth
      window.location.href = data.authorization_url;
    } catch (error) {
      console.error("Failed to start Google OAuth:", error);
      toast.error(error.message || "Falha ao conectar com Google Calendar");
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/auth/google/disconnect?user_id=${userId}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        toast.success("Google Calendar desconectado");
        setStatus(prev => ({ ...prev, connected: false, email: null }));
      } else {
        throw new Error("Failed to disconnect");
      }
    } catch (error) {
      console.error("Failed to disconnect Google Calendar:", error);
      toast.error("Falha ao desconectar");
    }
  };

  if (status.loading) {
    return (
      <div className="p-4 bg-nectar-card border border-nectar-border rounded-2xl">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          <span className="text-slate-400">Verificando conexão...</span>
        </div>
      </div>
    );
  }

  // Integration not configured (missing credentials)
  if (!status.configured) {
    return (
      <div className="p-4 bg-nectar-card border border-nectar-border rounded-2xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5 text-slate-500" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-white">Google Calendar</h3>
            <p className="text-sm text-slate-500 mt-1">
              Integração não configurada. Aguardando credenciais OAuth.
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <AlertCircle className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-amber-400">Pendente</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-nectar-card border border-nectar-border rounded-2xl hover:border-nectar-border/80 transition-colors"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          status.connected 
            ? "bg-brand-green/10 border border-brand-green/30" 
            : "bg-white/5 border border-nectar-border"
        }`}>
          <Calendar className={`w-5 h-5 ${status.connected ? "text-brand-green" : "text-slate-400"}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-white">Google Calendar</h3>
            <AnimatePresence>
              {status.connected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="w-5 h-5 rounded-full bg-brand-green/20 flex items-center justify-center"
                >
                  <Check className="w-3 h-3 text-brand-green" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {status.connected ? (
            <p className="text-sm text-slate-400 mt-1 truncate">
              Conectado como <span className="text-brand-cyan">{status.email}</span>
            </p>
          ) : (
            <p className="text-sm text-slate-500 mt-1">
              Conecte para sincronizar eventos e criar agendamentos via Nexo.
            </p>
          )}
        </div>

        {/* Action Button */}
        <div className="flex-shrink-0">
          {status.connected ? (
            <motion.button
              onClick={handleDisconnect}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-4 py-2 bg-status-error/10 border border-status-error/30 text-status-error rounded-xl text-sm font-medium hover:bg-status-error/20 transition-colors flex items-center gap-2"
              data-testid="google-disconnect-btn"
            >
              <X className="w-4 h-4" />
              Desconectar
            </motion.button>
          ) : (
            <motion.button
              onClick={handleConnect}
              disabled={isConnecting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-4 py-2 bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan rounded-xl text-sm font-medium hover:bg-brand-cyan/20 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="google-connect-btn"
            >
              {isConnecting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Conectando...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4" />
                  Conectar
                </>
              )}
            </motion.button>
          )}
        </div>
      </div>

      {/* Features list when not connected */}
      {!status.connected && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-4 pt-4 border-t border-nectar-border"
        >
          <p className="text-xs text-slate-500 mb-2">Com a integração você poderá:</p>
          <ul className="text-xs text-slate-400 space-y-1">
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-brand-cyan" />
              Ver sua agenda do dia falando com Nexo
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-brand-cyan" />
              Criar eventos por voz ou texto
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-brand-cyan" />
              Receber lembretes de compromissos
            </li>
          </ul>
        </motion.div>
      )}
    </motion.div>
  );
}
